import { useMemo } from 'react';
import { useOffSetTop, useResponsive, useCookieConsent } from '@emfood/shared';

import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';

import { bgBlur } from 'src/theme/css';
import { BANNER_SYSTEM_MESSAGE } from 'src/config-global';

import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { BannerBox } from 'src/components/banner/Banner';
import { useSettingsContext } from 'src/components/settings';

import CartButton from '../common/cart-button';
import AccountPopover from '../common/account-popover';
import { NAV, HEADER, COOKIE_BANNER } from '../config-layout';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();

  const { shouldDisplayCookieBanner } = useCookieConsent();

  const smDown = useResponsive('down', 'sm');

  const systemMassage = useMemo(() => BANNER_SYSTEM_MESSAGE, []);

  const settings = useSettingsContext();

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!lgUp && (
        <IconButton aria-label="Toggle Navigation Panel" onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <CartButton />
        {/* <SettingsButton /> */}

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        ...(!shouldDisplayCookieBanner
          ? {}
          : {
              mt: smDown ? COOKIE_BANNER.MOBILE : COOKIE_BANNER.DESKTOP,
            }),
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
          height: HEADER.H_DESKTOP,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
      {systemMassage && <BannerBox message={systemMassage} />}
      {/* <EditInfoBox /> */}
    </AppBar>
  );
}
